import { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { Box, Breadcrumbs, Link, Paper, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import Alert from "../../layout/Alert";
import AppButton from "../../Common/Button/Button";
import AppIconButton from "../../Common/Button/IconButton";
import FormInput from "../../Common/FormInputs/MuiTextField";
import DataTable from "../../Common/DataTable/DataTable";
import IncrementalPagination from "../../Common/DataTable/IncrementalPagination";
import ScreenLoader from "../../Common/ScreenLoader/ScreenLoader";
import FullScreenLoader from "../../Common/ScreenLoader/FullScreenLoader";
import EmptyState from "../../Common/EmptyState/EmptyState";
import { actionButtonsRenderer, textRenderer } from "../../Common/DataTable/CellRenderers";
import { SectionHeading } from "../../Common/Typography/AppTypography";
import { selectMenuProps } from "../../../utils/selectMenuProps";
import { format } from "date-fns";
import {
  createEmptyNotificationTemplate,
  getNotificationPermission,
  getNotificationTemplateTypeLabel,
  notificationTemplateListPageSize,
  notificationTemplateListPageSizeOptions,
  notificationTemplateTypeOptions,
  notificationTemplateTypes,
} from "../notificationConstant";
import { clonePlain, getTemplateId } from "../notificationFormUtils";
import {
  createNotificationTemplate,
  deleteNotificationTemplate,
  getNotificationTemplateById,
  getNotificationTemplates,
  setNotificationTemplateSearch,
  updateNotificationTemplate,
} from "../../../action/notificationTemplate";
import { setAlert } from "../../../action/alert";
import TemplateForm from "./TemplateForm";
import TemplateView from "./TemplateView";
import DeleteTemplateDialog from "./DeleteTemplateDialog";
import {
  emailCompactFormControlSx,
  emailFocusOutlineSx,
  emailSectionCardSx,
} from "../emailFormStyles";
import { useTheme } from "@mui/material/styles";
import { useDesignTokens } from "../../../theme/useDesignTokens";

const ALL_TYPES = "all";

const TemplateList = ({
  allowedTypes,
  onLoadTemplate,
  onTemplateScreenChange,
  templates,
  templatesLoading,
  templatesError,
  templateSearch,
  templateMutationInProgress,
  userRoles,
  getNotificationTemplates,
  getNotificationTemplateById,
  createNotificationTemplate,
  updateNotificationTemplate,
  deleteNotificationTemplate,
  setNotificationTemplateSearch,
  setAlert,
}) => {
  const theme = useTheme();
  const { colors } = useDesignTokens();
  const primaryColor = colors?.primary?.main || theme.palette.primary.main;
  const focusOutlineSx = emailFocusOutlineSx(primaryColor);
  const controlSx = emailCompactFormControlSx(theme);

  const [mode, setMode] = useState("list");
  const [activeTemplate, setActiveTemplate] = useState(null);
  const [typeFilter, setTypeFilter] = useState(ALL_TYPES);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(notificationTemplateListPageSize);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const typeOptions = [
    { value: ALL_TYPES, label: "All types" },
    ...(allowedTypes?.length
      ? notificationTemplateTypeOptions.filter((option) => allowedTypes.includes(option.value))
      : notificationTemplateTypeOptions),
  ];

  const canManageTemplates = getNotificationPermission(userRoles, "notificationTemplates");

  const refreshList = () => {
    if (!canManageTemplates) return;
    getNotificationTemplates({
      channel: typeFilter && typeFilter !== ALL_TYPES ? typeFilter : undefined,
      pageIndex: page,
      pageSize: rowsPerPage,
    });
  };

  useEffect(() => {
    refreshList();
  }, [typeFilter, page, rowsPerPage, canManageTemplates]);

  useEffect(() => {
    onTemplateScreenChange?.(mode !== "list");
    return () => onTemplateScreenChange?.(false);
  }, [mode, onTemplateScreenChange]);

  const filteredTemplates = useMemo(() => {
    return (templates || []).filter((template) => {
      if (allowedTypes?.length && !allowedTypes.includes(template.notificationType)) {
        return false;
      }
      const query = (templateSearch || "").trim().toLowerCase();
      if (query && !(template.templateName || "").toLowerCase().includes(query)) {
        return false;
      }
      return true;
    });
  }, [templates, allowedTypes, templateSearch]);

  const usesClientPaging = filteredTemplates.length > rowsPerPage;
  const pagedTemplates = useMemo(() => {
    if (!usesClientPaging) return filteredTemplates;
    const start = page * rowsPerPage;
    return filteredTemplates.slice(start, start + rowsPerPage);
  }, [filteredTemplates, page, rowsPerPage, usesClientPaging]);
  const isLastPage = usesClientPaging
    ? (page + 1) * rowsPerPage >= filteredTemplates.length
    : pagedTemplates.length < rowsPerPage;

  useEffect(() => {
    if (templatesLoading) return;
    if (filteredTemplates.length > rowsPerPage) {
      const maxPage = Math.max(0, Math.ceil(filteredTemplates.length / rowsPerPage) - 1);
      if (page > maxPage) setPage(maxPage);
      return;
    }
    if (page > 0 && filteredTemplates.length === 0) {
      setPage((current) => Math.max(0, current - 1));
    }
  }, [templatesLoading, filteredTemplates.length, rowsPerPage, page]);

  const openTemplate = async (row, nextMode) => {
    const templateId = getTemplateId(row);
    if (!templateId) return;
    setDetailLoading(true);
    const result = await getNotificationTemplateById(templateId);
    setDetailLoading(false);
    if (!result?.success || !result.data) {
      setAlert(result?.message || "Failed to load template", "danger");
      return;
    }
    const template = clonePlain(result.data);
    if (nextMode === "load") {
      onLoadTemplate?.(template);
      return;
    }
    setActiveTemplate(template);
    setMode(nextMode);
  };

  const columns = [
    { key: "templateName", label: "Template Name", minWidth: 180, render: textRenderer },
    {
      key: "notificationType",
      label: "Notification Type",
      minWidth: 150,
      render: (value) => textRenderer(getNotificationTemplateTypeLabel(value)),
    },
    {
      key: "lastUpdated",
      label: "Last Updated",
      minWidth: 160,
      render: (value) =>
        textRenderer(
          value && !Number.isNaN(new Date(value).getTime())
            ? format(new Date(value), "dd MMM yyyy hh:mm a")
            : "-"
        ),
    },
    {
      key: "actions",
      label: "Actions",
      align: "left",
      minWidth: 160,
      render: actionButtonsRenderer(
        [
          {
            id: "view",
            icon: VisibilityIcon,
            tooltip: "View",
            variant: "info",
            onClick: (row) => openTemplate(row, "view"),
          },
          {
            id: "edit",
            icon: EditIcon,
            tooltip: "Update",
            variant: "primary",
            onClick: (row) => openTemplate(row, "edit"),
          },
          {
            id: "load",
            icon: FileUploadIcon,
            tooltip: "Load",
            variant: "success",
            condition: (row) =>
              row.notificationType === notificationTemplateTypes.push.value ||
              row.notificationType === notificationTemplateTypes.email.value,
            onClick: (row) => openTemplate(row, "load"),
          },
          {
            id: "delete",
            icon: DeleteIcon,
            tooltip: "Delete",
            variant: "danger",
            onClick: (row) => setDeleteTarget(row),
          },
        ],
        { justifyContent: "flex-start" }
      ),
    },
  ];

  const handleSave = async (payload) => {
    const templateId = getTemplateId(activeTemplate);
    const result =
      mode === "edit" && templateId
        ? await updateNotificationTemplate(templateId, payload)
        : await createNotificationTemplate(payload);
    if (result?.success) {
      setMode("list");
      setActiveTemplate(null);
      refreshList();
    }
  };

  const goToTemplateList = () => {
    setMode("list");
    setActiveTemplate(null);
  };

  const TemplateScreenHeader = ({ current }) => (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.75,
        mt: 1,
        mb: 1.5,
        minWidth: 0,
      }}
    >
      <AppIconButton
        variant="ghost"
        size="small"
        icon={<ArrowBackIcon fontSize="small" />}
        tooltip="Back to Templates"
        onClick={goToTemplateList}
        aria-label="Back to Templates"
        sx={{ flexShrink: 0 }}
      />
      <Breadcrumbs
        separator={<NavigateNextIcon fontSize="small" />}
        aria-label="breadcrumb"
        sx={{ minWidth: 0, "& .MuiBreadcrumbs-ol": { flexWrap: "wrap" } }}
      >
        <Link
          component="button"
          type="button"
          underline="hover"
          onClick={goToTemplateList}
          sx={{
            color: primaryColor,
            fontWeight: 600,
            fontSize: "0.875rem",
            border: "none",
            background: "none",
            cursor: "pointer",
            p: 0,
          }}
        >
          Templates
        </Link>
        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
          {current}
        </Typography>
      </Breadcrumbs>
    </Box>
  );

  const handleDelete = async () => {
    const templateId = getTemplateId(deleteTarget);
    if (!templateId) return;
    const result = await deleteNotificationTemplate(templateId);
    if (result?.success) {
      setDeleteTarget(null);
      refreshList();
    }
  };

  if (!canManageTemplates) {
    return (
      <Box sx={{ py: 2 }}>
        <SectionHeading>Insufficient Permission</SectionHeading>
      </Box>
    );
  }

  if (mode === "create" || mode === "edit") {
    return (
      <Box sx={{ width: "100%", minWidth: 0, pb: 2 }}>
        <Alert />
        <TemplateScreenHeader
          current={mode === "edit" ? "Update Template" : "Create Template"}
        />
        <TemplateForm
          mode={mode}
          initialTemplate={
            activeTemplate ||
            createEmptyNotificationTemplate(
              allowedTypes?.[0] || (typeFilter !== ALL_TYPES ? typeFilter : undefined)
            )
          }
          allowedTypes={allowedTypes}
          saving={templateMutationInProgress}
          onSave={handleSave}
          onCancel={goToTemplateList}
          onError={(message) => setAlert(message, "danger")}
        />
      </Box>
    );
  }

  if (mode === "view") {
    return (
      <Box sx={{ width: "100%", minWidth: 0, pb: 2 }}>
        <Alert />
        <TemplateScreenHeader current="View Template" />
        <TemplateView
          template={activeTemplate}
          onClose={goToTemplateList}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", minWidth: 0, pb: 2 }}>
      <Alert />
      {(detailLoading || templateMutationInProgress) && (
        <FullScreenLoader message={detailLoading ? "Loading template..." : "Deleting template..."} />
      )}
      <Paper elevation={0} sx={{ ...emailSectionCardSx, mt: 1, overflow: "hidden" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            gap: 1.5,
            flexWrap: "wrap",
            alignItems: "center",
            mb: 2,
          }}
        >
          <SectionHeading sx={{ mb: 0 }}>Templates</SectionHeading>
          <AppButton
            variant="primary"
            size="medium"
            startIcon={<AddIcon />}
            onClick={() => {
              setActiveTemplate(
                createEmptyNotificationTemplate(
                  allowedTypes?.[0] || (typeFilter !== ALL_TYPES ? typeFilter : undefined)
                )
              );
              setMode("create");
            }}
          >
            Create Template
          </AppButton>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 1.5,
            mb: 2,
          }}
        >
          <Box sx={{ ...controlSx, width: { xs: "100%", sm: 280 }, maxWidth: { xs: "100%", sm: 280 } }}>
            <FormInput
              type="text"
              size="small"
              fullWidth
              placeholder="Search by template name"
              value={templateSearch}
              onChange={(e) => {
                setNotificationTemplateSearch(e.target.value);
                setPage(0);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") refreshList();
              }}
              sx={focusOutlineSx}
            />
          </Box>
          <Box sx={{ ...controlSx, width: { xs: "100%", sm: 220 }, maxWidth: { xs: "100%", sm: 220 } }}>
            <FormInput
              type="select"
              size="small"
              fullWidth
              value={typeFilter || ALL_TYPES}
              onChange={(e) => {
                setTypeFilter(e.target.value || ALL_TYPES);
                setPage(0);
              }}
              options={typeOptions}
              displayEmpty
              renderValue={(value) =>
                typeOptions.find((option) => option.value === value)?.label || "All types"
              }
              MenuProps={selectMenuProps}
              sx={focusOutlineSx}
            />
          </Box>
          <AppButton
            variant="ghost"
            size="medium"
            startIcon={<SearchIcon />}
            onClick={() => {
              setPage(0);
              refreshList();
            }}
          >
            Search
          </AppButton>
        </Box>

        {templatesLoading ? (
          <ScreenLoader
            height={220}
            message="Loading templates..."
            sx={{ bgcolor: "transparent" }}
          />
        ) : templatesError ? (
          <EmptyState
            height={220}
            message={templatesError}
            sx={{ bgcolor: "transparent" }}
          />
        ) : (
          <>
            <DataTable
              columns={columns}
              data={pagedTemplates}
              rowKey="id"
              minWidth="100%"
              fullWidth
              tableContainerSx={{
                boxShadow: "none",
                borderRadius: 0,
              }}
              msgForDisplay={
                templateSearch || (typeFilter && typeFilter !== ALL_TYPES)
                  ? "No templates match this search."
                  : "No templates yet. Create a template to get started."
              }
            />
            <IncrementalPagination
              page={page}
              pageSize={rowsPerPage}
              dataLength={pagedTemplates.length}
              isLastPage={isLastPage}
              onPageChange={setPage}
              onPageSizeChange={(size) => {
                setRowsPerPage(size);
                setPage(0);
              }}
              pageSizeOptions={notificationTemplateListPageSizeOptions}
              sx={{ m: 0, mt: 1.5, px: 0 }}
            />
          </>
        )}
      </Paper>

      <DeleteTemplateDialog
        open={Boolean(deleteTarget)}
        template={deleteTarget}
        loading={templateMutationInProgress}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
      />
    </Box>
  );
};

const mapStateToProps = (state) => ({
  templates: state.notification.templates,
  templatesLoading: state.notification.templatesLoading,
  templatesError: state.notification.templatesError,
  templateSearch: state.notification.templateSearch,
  templateMutationInProgress: state.notification.templateMutationInProgress,
  userRoles: state.auth.roles,
});

const mapDispatchToProps = (dispatch) => ({
  getNotificationTemplates: (filters) => dispatch(getNotificationTemplates(filters)),
  getNotificationTemplateById: (templateId) => dispatch(getNotificationTemplateById(templateId)),
  createNotificationTemplate: (payload) => dispatch(createNotificationTemplate(payload)),
  updateNotificationTemplate: (id, payload) => dispatch(updateNotificationTemplate(id, payload)),
  deleteNotificationTemplate: (id) => dispatch(deleteNotificationTemplate(id)),
  setNotificationTemplateSearch: (search) => dispatch(setNotificationTemplateSearch(search)),
  setAlert: (message, status) => dispatch(setAlert(message, status)),
});

export default connect(mapStateToProps, mapDispatchToProps)(TemplateList);
